import { useQuery } from "@apollo/client"
import { gql } from "../gql"
import {
  BenefitTier,
  CalendarDuration,
  CalendarDurationType,
  CountryIntelligenceOysterScore,
  CountryIntelligenceRulesTimeOffType,
} from "@oysterhr/schema"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "react-i18next"
import { convertToDays, maxDuration, minDuration, sortByDuration } from "../utils/duration"

export const OYSTER_INTELLIGENCE_COUNTRY_BENEFITS_OPTIONS_QUERY = gql(`
  query OysterIntelligenceCountryBenefitsOptionsQuery($countryCode: String!) {
    intelligenceBenefits(countryCode: $countryCode) {
      effectiveNationalHolidaysForYear
      countryBenefits(page: 1, perPage: 100) {
        collection {
          tier
          benefit
          comment
        }
      }
      timeOffRules(page: 1, perPage: 100) {
        collection {
          type
          oysterScore
          amountPerYear {
            unit
            value
          }
        }
      }
    }
  }
`)

export enum BenefitSection {
  BASE = "BASE",
  ADDITIONAL = "ADDITIONAL",
}

type InputBenefit = {
  tier: BenefitTier
  benefit: string
  comment?: string | null
}

type TimeOffRule = {
  type?: CountryIntelligenceRulesTimeOffType | null
  oysterScore?: CountryIntelligenceOysterScore | null
  amountPerYear?: CalendarDuration | null
}

type InputData = {
  effectiveNationalHolidaysForYear?: number
  countryBenefits?: {
    collection: InputBenefit[]
  }
  timeOffRules?: {
    collection: TimeOffRule[]
  }
}

// Output types
export type BenefitsDetail = {
  duration: CalendarDuration
  durationEnd?: CalendarDuration
  oysterScore: CountryIntelligenceOysterScore
}

export type OutputBenefit = {
  section: BenefitSection
  category: string
  note?: string | null
  details?: BenefitsDetail[]
}

export type BenefitsOutputData = {
  intelligenceBenefits: OutputBenefit[]
  hasBenefits: boolean
}

function transformBenefitsData(input: InputData, t: TFunction): BenefitsOutputData {
  const benefitsMap: Map<string, OutputBenefit> = new Map()

  // Process effectiveNationalHolidaysForYear
  if (input.effectiveNationalHolidaysForYear && input.effectiveNationalHolidaysForYear > 0) {
    benefitsMap.set("National public holidays", {
      section: BenefitSection.BASE,
      category: t("nationalPublicHolidays"),
      details: [
        {
          duration: {
            unit: CalendarDurationType.Days,
            value: input.effectiveNationalHolidaysForYear,
          },
          oysterScore: CountryIntelligenceOysterScore.Statutory,
        },
      ],
    })
  }

  // Process countryBenefits
  input.countryBenefits?.collection?.forEach(benefit => {
    if (benefit.tier === BenefitTier.Statutory || benefit.tier === BenefitTier.Base) {
      benefitsMap.set(benefit.benefit, {
        section:
          benefit.tier === BenefitTier.Statutory ? BenefitSection.BASE : BenefitSection.ADDITIONAL,
        category: benefit.benefit,
        note: benefit.comment,
      })
    }
  })

  // Process timeOffRules
  const timeOffBenefits: Map<
    string,
    Map<CountryIntelligenceOysterScore, CalendarDuration[]>
  > = new Map()

  input.timeOffRules?.collection?.forEach(rule => {
    if (rule.type && rule.amountPerYear) {
      if (!timeOffBenefits.has(rule.type)) {
        timeOffBenefits.set(rule.type, new Map())
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const timeOffBenefitMap = timeOffBenefits.get(rule.type)!
      const score = rule.oysterScore as CountryIntelligenceOysterScore
      if (!timeOffBenefitMap.has(score)) {
        timeOffBenefitMap.set(score, [])
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timeOffBenefitMap.get(score)!.push(rule.amountPerYear)
    }
  })

  // Process all time off benefits
  timeOffBenefits.forEach((timeOffBenefitMap, benefitType) => {
    timeOffBenefitMap.forEach((durations, score) => {
      const sortedDurations = durations.sort(sortByDuration)
      const minDurationValue = minDuration(sortedDurations)
      const maxDurationValue = maxDuration(sortedDurations)

      if (minDurationValue && maxDurationValue) {
        const detail: BenefitsDetail = {
          duration: minDurationValue,
          oysterScore: score,
        }

        if (
          sortedDurations.length > 1 &&
          convertToDays(maxDurationValue.unit, maxDurationValue.value ?? 0) !==
            convertToDays(minDurationValue.unit, minDurationValue.value ?? 0)
        ) {
          detail.durationEnd = maxDurationValue
        }

        const section =
          score === CountryIntelligenceOysterScore.Statutory
            ? BenefitSection.BASE
            : BenefitSection.ADDITIONAL

        const key = `${section === BenefitSection.BASE ? "Statutory" : "Additional"} ${benefitType}`

        if (!benefitsMap.has(key)) {
          benefitsMap.set(key, {
            section,
            category: t(`timeOffRules.${benefitType}`),
            details: [],
          })
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        benefitsMap.get(key)!.details!.push(detail)
      }
    })
  })

  const countryBenefits = input?.countryBenefits?.collection || []

  return {
    intelligenceBenefits: Array.from(benefitsMap.values()),
    hasBenefits: countryBenefits.length > 0,
  }
}

export const useBenefitsOptionsForCountry = (params: { countryCode?: string }) => {
  const { t } = useTranslation("salary_insights_tool/benefits")
  const { data, loading, error } = useQuery(OYSTER_INTELLIGENCE_COUNTRY_BENEFITS_OPTIONS_QUERY, {
    variables: {
      countryCode: params?.countryCode as string,
    },
    skip: !params?.countryCode,
  })

  const benefitsList = useMemo(
    () => (data?.intelligenceBenefits ? transformBenefitsData(data.intelligenceBenefits, t) : null),
    [data, t],
  )

  return {
    benefitsList,
    loading,
    error,
  }
}
